import distributorService from "@/views/distributor/distributorService";
import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiHosts } from "@/api/constants/apiHosts";

import type { Distributor, DistributorNew, DistributorQuery } from "./distributor";
import type { AxiosResponse } from "axios";
import type { RequestValidationError } from "@/helpers/requestValidationError";

const URL = "distributors";
const ENTITY_NAME = "Distributor";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_ODR_API_BASE_URL,
};
const api = apiService<Distributor>(apiHosts.ODR, axiosParams);
const canDeleteApi = apiService<RequestValidationError>(apiHosts.ODR, axiosParams);

const getDistributors = (params: DistributorQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getDistributorById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewDistributor = async (ids: DistributorIds) => {
  const defaultApp = {
    data: {
      ...distributorService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<DistributorNew, any>;

  return defaultApp;
};

type DistributorIds = {
  id?: string | number;
};
const getDistributorByIdOrNew = (ids: DistributorIds, config: PagingApiAndNotifierConfig = {}) => {
  if (!ids.id) {
    return getNewDistributor(ids);
  } else {
    return getDistributorById(ids.id, config) as Promise<AxiosResponse<DistributorNew, any>>;
  }
};

const createDistributor = (distributor: Distributor, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, distributor, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateDistributor = (distributor: Distributor, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + distributor.id, distributor, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Distributor} distributor This cannot be a 'ref' to a distributor
 */
const saveDistributor = (distributor: Distributor, config: PagingApiAndNotifierConfig = {}) => {
  if (distributor.id) {
    return updateDistributor(distributor, config);
  } else {
    return createDistributor(distributor, config);
  }
};

const deleteDistributorById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const canDeleteDistributor = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return canDeleteApi.delete(URL + "/" + id + "?" + "validateOnly=" + true, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  deleteDistributorById,
  getDistributors,
  getDistributorById,
  getNewDistributor,
  getDistributorByIdOrNew,
  createDistributor,
  updateDistributor,
  saveDistributor,
  canDeleteDistributor,
};
